<template>
    <div>
        <Toast />
        <DataTable :selectionMode="isMobile ? null : 'single'" :value="flattenedData" dataKey="id" scrollable
            :scrollHeight="'72svh'" :loading="loading" class="rounded-xl shadow-sm md:col-span-1 mt-5" fluid
            v-model:selection="selectedRow" v-model:expandedRows="expandedRows" @selection-change="onSelectionChange"
            sortField="totalOperatorRevenue" :sortOrder="-1" showFooter>
            <Column :expander="true" :headerStyle="{
                ...headerStyles,
                width: '3rem'
            }" :bodyStyle="{
                paddingLeft: '0',
                paddingRight: '0'
            }" frozen alignFrozen="left">
                <template #body="slotProps">
                    <button v-if="isExpandable(slotProps.data)" @click="toggleRow(slotProps.data)"
                        class="p-2 rounded-full hover:bg-gray-100">
                        <i :class="isExpanded(slotProps.data) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
                    </button>
                </template>
            </Column>
            <Column header="Partner" :headerStyle="headerStyles" :bodyStyle="{
                paddingLeft: '2',
                paddingRight: '0'
            }" frozen alignFrozen="left">
                <template #body="{ data }">
                    <span :class="{ 'font-semibold': data.isPartnerTotal }">
                        <a :href="data.loginUrl" target="_blank" class="text-blue-600 hover:underline">
                            {{ data.partner }}
                        </a>
                        <Button icon="pi pi-user" severity="secondary" @click="copyToClipboard(data.username)"
                            class="p-button-text p-button-sm mt-1 ml-2" v-tooltip.top="'Copy username'" />
                        <Button icon="pi pi-key" severity="secondary" @click="copyToClipboard(data.password)"
                            class="p-button-text p-button-sm mt-1" v-tooltip.top="'Copy password'" />
                        <Button v-if="!isExpandable(data) && data.payerId" icon="pi pi-money-bill" severity="primary" @click="showPayerInfo(data.payerDetails, data.advertiserName)"
                            class="p-button-text p-button-sm mt-1" v-tooltip.top="'Show payer details'" />
                        <Tag v-if="!isExpandable(data) && !data.payerId" severity="danger" value="Not Set" />
                    </span>
                </template>
                <template #footer>Totals</template>§
            </Column>
            <Column header="Advertiser" :headerStyle="headerStyles" :bodyStyle="{
                paddingLeft: '2',
                paddingRight: '0'
            }" frozen alignFrozen="left">
                <template #body="slotProps">
                    <span v-if="!slotProps.data.isPartnerTotal">{{ slotProps.data.advertiserName }}</span>
                    <span v-else class="italic">
                        {{ getAdvertiserNames(slotProps.data.partner) }}
                    </span>
                </template>
            </Column>
            <Column header="CPAs" :headerStyle="headerStyles">
                <template #body="slotProps">
                    <span :class="{ 'font-semibold': slotProps.data.isPartnerTotal }">
                        {{ slotProps.data.CPACount }}
                    </span>
                </template>
                <template #footer>
                    <span class="font-bold">{{ totals.CPACount }}</span>
                </template>
            </Column>
            <Column header="Casino GGR" :headerStyle="headerStyles">
                <template #body="slotProps">
                    <span :class="{ 'font-semibold': slotProps.data.isPartnerTotal }">
                        {{ formatCurrency(slotProps.data.netRevenue) }}
                    </span>
                </template>
                <template #footer>
                    <span class="font-bold">{{ formatCurrency(totals.netRevenue) }}</span>
                </template>
            </Column>
            <Column header="Publisher Revenue" :headerStyle="headerStyles">
                <template #body="{data}">
                    <div :class="{
                        'income-cell': data.customerTotalRevenue > 0,
                        'font-semibold': data.isPartnerTotal
                    }">
                        <p>{{ formatCurrency(data.customerTotalRevenue) }}</p>
                    </div>
                </template>
                <template #footer>
                    <span class="font-bold income-cell">{{ formatCurrency(totals.customerTotalRevenue) }}</span>
                </template>
            </Column>
            <Column header="Our Revenue" :headerStyle="headerStyles">
                <template #body="{data}">
                    <div :class="{
                        'income-cell': data.ourTotalRevenue > 0,
                        'font-semibold': data.isPartnerTotal
                    }">
                        <p>{{ formatCurrency(data.ourTotalRevenue) }}</p>
                    </div>
                </template>
                <template #footer>
                    <span class="font-bold income-cell">{{ formatCurrency(totals.ourTotalRevenue) }}</span>
                </template>
            </Column>
            <Column header="Operator Revenue" sortable :headerStyle="headerStyles">
                <template #body="{data}">
                    <div :class="{
                        'income-cell': data.totalOperatorRevenue > 0,
                        'font-semibold': data.isPartnerTotal
                    }">
                        <p>{{ formatCurrency(data.totalOperatorRevenue) }}</p>
                    </div>
                </template>
                <template #footer>
                    <span class="font-bold income-cell">{{ formatCurrency(totals.totalOperatorRevenue) }}</span>
                </template>
            </Column>
            <template #expansion="slotProps">
                <div v-if="slotProps.data.isPartnerTotal" class="pl-[25%] pr-4 py-3">
                    <DataTable :value="getPartnerAdvertisers(slotProps.data.partner)" class="shadow-sm"
                        sortField="totalOperatorRevenue" :sortOrder="-1">
                        <Column 
                            header="Advertiser" 
                            :headerStyle="{
                                ...headerStyles,
                                width: '180px',
                                minWidth: '180px'
                            }"
                        >
                            <template #body="{ data }">
                                <div class="flex items-center gap-2">
                                    <div>{{ data.advertiserName }}</div>
                                    <Button v-if="data.advertiserPayerId" 
                                        icon="pi pi-money-bill" 
                                        severity="primary" 
                                        @click="showPayerInfo(data.payerDetails, data.advertiserName)"
                                        class="p-button-text p-button-sm" 
                                        v-tooltip.top="'Show payer details'" 
                                    />
                                    <Tag v-else severity="danger" value="Not Set" />
                                </div>
                            </template>
                        </Column>
                        <Column field="CPACount" header="CPAs" :headerStyle="headerStyles"></Column>
                        <Column field="netRevenue" header="Casino GGR" :headerStyle="headerStyles">
                            <template #body="{data}">
                                {{ formatCurrency(data.netRevenue) }}
                            </template>
                        </Column>
                        <Column header="Publisher Revenue" :headerStyle="headerStyles">
                            <template #body="{data}">
                                <div
                                    :class="{ 'income-cell': data.customerTotalRevenue > 0 }">
                                    {{ formatCurrency(data.customerTotalRevenue) }}
                                </div>
                            </template>
                        </Column>
                        <Column header="Our Revenue" :headerStyle="headerStyles">
                            <template #body="{data}">
                                <div
                                    :class="{ 'income-cell': data.ourTotalRevenue > 0 }">
                                    {{ formatCurrency(data.ourTotalRevenue) }}
                                </div>
                            </template>
                        </Column>
                        <Column field="totalOperatorRevenue" header="Operator Revenue" :headerStyle="headerStyles">
                            <template #body="{data}">
                                <div :class="{ 'income-cell': data.totalOperatorRevenue > 0 }">
                                    {{ formatCurrency(data.totalOperatorRevenue) }}
                                </div>
                            </template>
                        </Column>
                    </DataTable>
                </div>
            </template>
        </DataTable>
        <PayerDataDialog v-model:show="showPayerDialog" :payer="selectedPayer" />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { headerStyles } from '@/global/headerStyles';
import { formatCurrency } from '@/utils/currencies';
import { getTotalEarnings } from '@/utils/earnings';
import { useToast } from "primevue/usetoast";
import PayerDataDialog from '@/views/VoonixAdvertisers/PayerDataDialog.vue';
const toast = useToast();

// Props
const props = defineProps({
    advertiserData: {
        type: Array,
        required: true
    },
    loading: {
        type: Boolean,
        default: false
    },
    selectedMonth: {
        type: Date,
        default: null
    },
    isMobile: {
        type: Boolean,
        default: false
    }
});


// Reactive state
const selectedRow = ref(null);
const expandedRows = ref({});

// Flatten the data structure and add partner totals
const flattenedData = computed(() => {
    if (!props.advertiserData) return [];

    const result = [];
    let idCounter = 1;

    props.advertiserData.forEach(partnerGroup => {
        // Skip if no advertisers
        if (!partnerGroup.advertisers || partnerGroup.advertisers.length === 0) return;

        // If there's more than one advertiser, add a total row first
        if (partnerGroup.advertisers.length > 1) {
            // Calculate totals
            const partnerTotal = {
                partner: partnerGroup.partner,
                advertiserName: 'Total',
                isPartnerTotal: true,
                loginUrl: partnerGroup.loginUrl,
                username: partnerGroup.username,
                password: partnerGroup.password,
                id: idCounter++,
                // Sum up all numeric fields
                clicks: sumField(partnerGroup.advertisers, 'clicks'),
                uniqueClicks: sumField(partnerGroup.advertisers, 'uniqueClicks'),
                signups: sumField(partnerGroup.advertisers, 'signups'),
                FTD: sumField(partnerGroup.advertisers, 'FTD'),
                CPACount: sumField(partnerGroup.advertisers, 'CPACount'),
                deposits: sumField(partnerGroup.advertisers, 'deposits'),
                depositValue: sumField(partnerGroup.advertisers, 'depositValue'),
                netRevenue: sumField(partnerGroup.advertisers, 'netRevenue'),
                customerCPRevenue: sumField(partnerGroup.advertisers, 'customerCPRevenue'),
                customerRevshareRevenue: sumField(partnerGroup.advertisers, 'customerRevshareRevenue'),
                customerTotalRevenue: sumField(partnerGroup.advertisers, 'customerTotalRevenue'),
                ourCPRevenue: sumField(partnerGroup.advertisers, 'ourCPRevenue'),
                ourRevshareRevenue: sumField(partnerGroup.advertisers, 'ourRevshareRevenue'),
                ourTotalRevenue: sumField(partnerGroup.advertisers, 'ourTotalRevenue'),
                totalOperatorRevenue: sumField(partnerGroup.advertisers, 'totalOperatorRevenue')
            };

            result.push(partnerTotal);
        }

        // Add individual advertisers if there's only one
        if (partnerGroup.advertisers.length === 1) {
            const advertiser = {
                ...partnerGroup.advertisers[0],
                payerId: partnerGroup.advertisers[0].advertiserPayerId,
                payerDetails: partnerGroup.advertisers[0].payerDetails,
                partner: partnerGroup.partner,
                loginUrl: partnerGroup.loginUrl,
                username: partnerGroup.username,
                password: partnerGroup.password,
                isPartnerTotal: false,
                id: idCounter++
            };
            result.push(advertiser);
        }
    });

    return result;
});

// Helper function to sum a field across all advertisers
const sumField = (advertisers, field) => {
    return advertisers.reduce((sum, advertiser) => sum + (advertiser[field] || 0), 0);
};

// Helper function to get advertiser names for a partner
const getAdvertiserNames = (partnerName) => {
    const partnerGroup = props.advertiserData.find(group => group.partner === partnerName);
    if (!partnerGroup || !partnerGroup.advertisers) return '';

    // Get all advertiser names for this partner
    const names = partnerGroup.advertisers.map(adv => adv.advertiserName);

    // If there are more than 2 advertisers, show first 2 + count
    if (names.length > 2) {
        return `${names[0]}, ${names[1]} +${names.length - 2}`;
    }

    // Otherwise join all names with commas
    return names.join(', ');
};

// Helper function to get all advertisers for a partner
const getPartnerAdvertisers = (partnerName) => {
    const partnerGroup = props.advertiserData.find(group => group.partner === partnerName);
    return partnerGroup?.advertisers || [];
};

// Check if a row is expandable (only partner total rows are expandable)
const isExpandable = (row) => {
    return row.isPartnerTotal;
};

// Check if a row is expanded
const isExpanded = (row) => {
    return expandedRows.value[row.id];
};

// Toggle row expansion
const toggleRow = (row) => {
    // Create a new object to trigger reactivity
    const newExpandedRows = { ...expandedRows.value };

    if (newExpandedRows[row.id]) {
        // If already expanded, delete the property to collapse
        delete newExpandedRows[row.id];
    } else {
        // If not expanded, set to true to expand
        newExpandedRows[row.id] = true;
    }

    // Update the ref
    expandedRows.value = newExpandedRows;
};

// Methods
const onSelectionChange = (event) => {
    if (!props.isMobile) {
        expandedRows.value = event.value
            ? { [event.value.id]: true }
            : {};
    }
};

// Watch for selection changes
watch(selectedRow, (newValue) => {
    if (newValue) {
        expandedRows.value = { [newValue.id]: true };
    } else {
        expandedRows.value = {};
    }
});


const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({
            severity: 'success',
            summary: 'Copied',
            detail: 'Text copied to clipboard',
            life: 2000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy text',
            life: 3000
        });
    }
};

// Add this computed property after your other computed properties
const totals = computed(() => {
    if (!props.advertiserData) return {
        CPACount: 0,
        netRevenue: 0,
        customerTotalRevenue: 0,
        ourTotalRevenue: 0,
        totalOperatorRevenue: 0
    };

    return {
        CPACount: props.advertiserData.reduce((sum, group) => 
            sum + (group.advertisers?.reduce((subSum, adv) => subSum + (adv.CPACount || 0), 0) || 0), 0),
        netRevenue: props.advertiserData.reduce((sum, group) => 
            sum + (group.advertisers?.reduce((subSum, adv) => subSum + (adv.netRevenue || 0), 0) || 0), 0),
        customerTotalRevenue: props.advertiserData.reduce((sum, group) => 
            sum + (group.advertisers?.reduce((subSum, adv) => subSum + (adv.customerTotalRevenue || 0), 0) || 0), 0),
        ourTotalRevenue: props.advertiserData.reduce((sum, group) => 
            sum + (group.advertisers?.reduce((subSum, adv) => subSum + (adv.ourTotalRevenue || 0), 0) || 0), 0),
        totalOperatorRevenue: props.advertiserData.reduce((sum, group) => 
            sum + (group.advertisers?.reduce((subSum, adv) => subSum + (adv.totalOperatorRevenue || 0), 0) || 0), 0),
    };
});

const selectedPayer = ref(null);
const showPayerDialog = ref(false);

const showPayerInfo = (payer, advertiserName) => {
    selectedPayer.value = { ...payer, advertiserName };
    showPayerDialog.value = true;
};
</script>

<style>
.income-cell {
    color: #22c55e;
    /* text-green-500 */
    font-weight: 500;
}
</style>
